import React, { useState, useEffect } from 'react';
import { usePermissions, useRefresh, useVersion, useGetIdentity } from 'react-admin'; 
import RichTextInput from 'ra-input-rich-text'; 
import { Form, Field } from 'react-final-form';
import { rolesGlobal } from "../utils/constants";
import axios from 'axios';
import { envSettings } from "../settings/env";
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CustomBooleanInput } from "../customComponents/CustomBooleanInput";
import { CustomDialog } from "../customComponents/CustomDialog";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    maxWidth: 900,
    width: '100%',
    margin: '20px auto'
  },
  headerGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minHeight: '40px'
  },
  innerPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box'
  },
  policyPreviewCanvas: {
    lineHeight: '1.6',
    fontSize: '1rem',
    '& .ql-align-center': { textAlign: 'center' },
    '& .ql-align-right': { textAlign: 'right' },
    '& .ql-align-justify': { textAlign: 'justify' },
    '& .ql-direction-rtl': { direction: 'rtl', textAlign: 'right' }
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: theme.spacing(2)
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  metaInfo: {
    flex: 1
  },
  actionsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  editorContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& .ra-rich-text-input': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    },
    '& .ql-toolbar': {
      flexShrink: 0
    },
    '& .ql-container': { 
      flex: 1, 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0
    },
    '& .ql-editor': { 
      flex: 1, 
      overflowY: 'auto !important', 
      height: '100%'
    }
  },
  dialogContent: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 160px)', 
    maxHeight: 'calc(100vh - 160px)',
    overflow: 'hidden'
  },
  viewDialogContent: {
    padding: theme.spacing(3),
    overflowY: 'auto'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(5),
    width: '100%'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(10),
    width: '100%'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: '0.85rem',
    fontWeight: 'bold',
    marginRight: theme.spacing(1)
  },
  activeBadge: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  inactiveBadge: { backgroundColor: '#ffebee', color: '#c62828' },
  placeholderText: { color: theme.palette.text.disabled, fontStyle: 'italic' }
}));

const quillOptions = {
  modules: {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ direction: 'rtl' }], 
      [{ align: [] }], 
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  }
};

const PrivacyPolicySettings = () => {
  const { loadingPermissions, permissions } = usePermissions();
  const { identity, loading: loadingIdentity } = useGetIdentity(); 
  const refresh = useRefresh();
  const version = useVersion(); 
  
  const [policies, setPolicies] = useState([]);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(false); 
  const [editorText, setEditorText] = useState('');
  
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const classes = useStyles();

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(envSettings.baseUrl + '/policies');
      const data = response.data || [];
      setPolicies(data);
      
      if (data.length > 0) {
        const activePolicy = data.find(policy => policy.isActive === true);
        if (activePolicy) {
          setCurrentPolicy(activePolicy);
        } else {
          setCurrentPolicy(data[0]);
        }
      } else {
        setCurrentPolicy(null);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, [version]);

  const userRoles = Array.isArray(permissions) ? permissions : (permissions ? [permissions] : []);
  const isCoach = userRoles.includes(rolesGlobal.coach);
  
  const hasAdminClearance = userRoles.includes("STAFF USERS ADMINISTRATOR") || 
                            userRoles.includes("CONTENT MANAGER") || 
                            userRoles.includes("COMMERCIAL MANAGER") ||
                            userRoles.includes("SYSTEM ADMINISTRATOR");

  const isAllowedToEdit = hasAdminClearance && !isCoach;
  const hideActions = isAllowedToEdit === false;
  const hideEditor = hideActions === true || editingPolicy === false;

  const executeSavePolicy = async () => {
    setIsSaving(true);
    const isNewVersion = currentPolicy?.version === 'Auto-Incrementing';
    
    const currentUserId = identity && identity.id ? identity.id : null;

    try {
      await axios.post(envSettings.baseUrl + '/policies', { 
        text: editorText,
        userId: currentUserId
      });
      
      setDialogOpen(false);
      setEditingPolicy(false);
      
      setToastMessage(isNewVersion ? "Privacy policy version created successfully." : "Privacy policy updated successfully.");
      setToastOpen(true);
      
      refresh();
    } catch (error) {
      console.error('Error saving policy:', error);
      setToastMessage("An error occurred while attempting to save the policy template.");
      setToastOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (isSaving) return;
    setEditingPolicy(false);
    setEditorText('');
    if (policies && policies.length > 0 && currentPolicy?.version === 'Auto-Incrementing') {
      const activePolicy = policies.find(p => p.isActive === true);
      setCurrentPolicy(activePolicy || policies[0]);
    }
  };

  const handleViewPolicy = (policy) => {
    setEditingPolicy(false);
    setCurrentPolicy(policy);
    setViewingPolicy(true);
  };

  const handleCloseView = () => {
    setViewingPolicy(false);
  };

  const handleCreateNewPolicy = () => {
    if (!isAllowedToEdit) return; 
    setCurrentPolicy({ version: 'Auto-Incrementing' });
    setEditorText('<h1>Privacy Policy</h1><p>Enter your content here...</p>');
    setEditingPolicy(true);
  };

  const handleEditPolicy = (policy) => {
    if (!isAllowedToEdit) return;
    setCurrentPolicy(policy);
    setEditorText(policy.text);
    setEditingPolicy(true);
  };

  const handleConfirmSave = () => {
    if (!isAllowedToEdit) return;
    setDialogOpen(true);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
  };

  if (initialLoading || loadingPermissions || loadingIdentity) {
    return (
      <Paper className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress color="primary" size={40} />
          <Typography variant="body1" color="textSecondary" style={{ marginTop: 16 }}>
            Loading Privacy Policy Content
          </Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.container}>
      <div className={classes.headerGroup}>
        <Typography variant="h5" component="h2">
          Privacy Policy Settings
        </Typography>
        
        {!hideActions && (
          <Button variant="contained" color="primary" onClick={handleCreateNewPolicy}>
            Create New Version
          </Button>
        )}
      </div>

      <div className={classes.list}>
        <Typography variant="h6" gutterBottom style={{ paddingLeft: 8 }}>
          Version History Log
        </Typography>
        <hr style={{ opacity: 0.3, width: '100%', margin: '4px 0 12px 0' }} />
        
        {policies.map((policy) => (
          <div key={policy._id} className={classes.listItem}>
            <div className={classes.metaInfo}>
              <Typography variant="subtitle1">
                <strong>Version {policy.version}</strong> {policy.isActive && <span style={{ marginLeft: 8, color: '#2e7d32', fontSize: '0.85rem', fontWeight: 'bold' }}>(Active)</span>}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Edited by {policy.lastEditedBy} on {new Date(policy.lastEditDate).toLocaleString()}
              </Typography>
            </div>
            
            <div className={classes.actionsGroup}>
              {!hideActions ? (
                <CustomBooleanInput 
                  source="isActive" 
                  sortable={false} 
                  disabled={hideActions}
                  record={policy}
                  resource="policies"
                  label={policy.isActive ? "Active" : "Inactive"}
                  title="Activating Privacy Policy"
                  content="Activating this version will notify users to view the updated policy. Are you sure you want to continue?"
                />
              ) : (
                <span className={`${classes.statusBadge} ${policy.isActive ? classes.activeBadge : classes.inactiveBadge}`}>
                  {policy.isActive ? "Active" : "Inactive"}
                </span>
              )}

              <Button size="small" variant="outlined" onClick={() => handleViewPolicy(policy)}>
                View
              </Button>
              
              {!hideActions && (
                <Button size="small" variant="outlined" color="primary" onClick={() => handleEditPolicy(policy)}>
                  Edit
                </Button>
              )}
            </div>
          </div>
        ))}

        {policies.length === 0 && (
          <div className={classes.emptyState}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No privacy policy versions found in the database.
            </Typography>
            {!hideActions && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleCreateNewPolicy}
                style={{ marginTop: '16px' }}
              >
                Initialize Version 1.0
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog 
        open={viewingPolicy} 
        onClose={handleCloseView}
        fullScreen
        scroll="paper"
        aria-labelledby="view-dialog-title"
      >
        <DialogTitle id="view-dialog-title" style={{ padding: '24px 24px 12px 24px' }}>
          <Typography variant="h5" component="div">
            Viewing Policy Version {currentPolicy?.version} {currentPolicy?.isActive ? '(Active Version)' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers className={classes.viewDialogContent}>
          <Paper className={classes.innerPaper} variant="outlined">
            <div 
              className={classes.policyPreviewCanvas} 
              dangerouslySetInnerHTML={{ __html: currentPolicy?.text || '<em>No text content provided</em>' }} 
            />
          </Paper>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseView} variant="contained" color="primary" size="large">
            Close View
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!hideEditor} onClose={handleCancelEdit} fullScreen aria-labelledby="editor-dialog-title">
        <DialogTitle id="editor-dialog-title" style={{ padding: '24px 24px 12px 24px' }}>
          <Typography variant="h5" component="div">
            {currentPolicy?.version === 'Auto-Incrementing' ? "Creating New Privacy Policy Content" : `Editing Privacy Policy Content (Version ${currentPolicy?.version})`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <div className={classes.editorContainer}>
            <Form
              initialValues={{ policyText: editorText }}
              onSubmit={() => {}}
              render={({ form }) => {
                form.subscribe(
                  (state) => {
                    if (state.values.policyText !== undefined && state.values.policyText !== editorText) {
                      setEditorText(state.values.policyText);
                    }
                  },
                  { values: true }
                );

                return (
                  <form style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Field name="policyText" component={RichTextInput} options={quillOptions} label="" fullWidth />
                  </form>
                );
              }}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCancelEdit} variant="contained" color="secondary" size="large" disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary" size="large" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} style={{ color: '#fff' }} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomDialog
        open={dialogOpen}
        onClose={() => !isSaving && setDialogOpen(false)}
        title="Confirm Policy Changes"
        content="Editing the privacy policy will create a new version. Are you sure you want to continue?"
      >
        <Button onClick={() => setDialogOpen(false)} color="secondary" disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={executeSavePolicy} color="primary" autoFocus disabled={isSaving}>
          {isSaving ? "Saving..." : "Confirm"}
        </Button>
      </CustomDialog>

      <Snackbar open={toastOpen} autoHideDuration={4000} onClose={handleCloseToast} message={toastMessage} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} />
    </Paper>
  );
};

export default PrivacyPolicySettings;